import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { aiService } from '../../services/aiService';

const AIAssistantHub = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    openai: { enabled: false, model: 'gpt-4o-mini', usageLimit: 1000, hasKey: false, connectionStatus: 'not_tested' },
    gemini: { enabled: false, model: 'gemini-2.0-flash-exp', usageLimit: 1000, hasKey: false, connectionStatus: 'not_tested' },
    anthropic: { enabled: false, model: 'claude-3-haiku-20240307', usageLimit: 1000, hasKey: false, connectionStatus: 'not_tested' },
    groq: { enabled: false, model: 'llama-3.3-70b-versatile', usageLimit: 1000, hasKey: false, connectionStatus: 'not_tested' }
  });
  const [apiKeys, setApiKeys] = useState({ openai: '', gemini: '', anthropic: '', groq: '' });
  const [showKeys, setShowKeys] = useState({ openai: false, gemini: false, anthropic: false, groq: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  const providers = [
    {
      id: 'openai',
      name: 'OpenAI',
      icon: 'Sparkles',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      description: 'GPT-4, GPT-3.5 Turbo',
      models: [
        { value: 'gpt-4o', label: 'GPT-4o (Mais recente)' },
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Econômico)' },
        { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
        { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
      ],
      docsUrl: 'https://platform.openai.com/api-keys'
    },
    {
      id: 'gemini',
      name: 'Google Gemini',
      icon: 'Zap',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Gemini 2.0 Flash (Gratuito)',
      models: [
        { value: 'gemini-2.0-flash-exp', label: 'Gemini 2.0 Flash (Gratuito)' },
        { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
        { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' }
      ],
      docsUrl: 'https://aistudio.google.com/app/apikey'
    },
    {
      id: 'anthropic',
      name: 'Anthropic Claude',
      icon: 'Brain',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: 'Claude 3 Opus, Sonnet, Haiku',
      models: [
        { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
        { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
        { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' }
      ],
      docsUrl: 'https://console.anthropic.com/settings/keys'
    },
    {
      id: 'groq',
      name: 'Groq',
      icon: 'Cpu',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      description: 'LLaMA 3.3, Mixtral (Ultra-rápido)',
      models: [
        { value: 'llama-3.3-70b-versatile', label: 'LLaMA 3.3 70B (Recomendado)' },
        { value: 'llama-3.1-70b-versatile', label: 'LLaMA 3.1 70B' },
        { value: 'mixtral-8x7b-32768', label: 'Mixtral 8x7B' },
        { value: 'gemma2-9b-it', label: 'Gemma 2 9B' }
      ],
      docsUrl: 'https://console.groq.com/keys'
    }
  ];

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const savedConfig = await aiService?.getAIConfig();
      if (savedConfig && Object.keys(savedConfig)?.length > 0) {
        setConfig(savedConfig);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async (providerId) => {
    const apiKey = apiKeys?.[providerId];
    if (!apiKey) return;

    try {
      setSaving(prev => ({ ...prev, [providerId]: true }));
      const result = await aiService?.saveAPIKey(providerId, apiKey, {
        enabled: true,
        model: config?.[providerId]?.model,
        usageLimit: config?.[providerId]?.usageLimit
      });

      if (result?.success) {
        setConfig(prev => ({
          ...prev,
          [providerId]: { ...prev?.[providerId], hasKey: true, enabled: true }
        }));
        setApiKeys(prev => ({ ...prev, [providerId]: '' }));
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'success',
            message: `Chave ${providers.find(p => p.id === providerId)?.name} salva com sucesso`,
            progress: 100
          }
        }));
      }
    } catch (error) {
      console.error('Error saving key:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao salvar chave API',
          progress: 100
        }
      }));
    } finally {
      setSaving(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const handleTestConnection = async (providerId) => {
    const apiKey = apiKeys?.[providerId];
    if (!apiKey && !config?.[providerId]?.hasKey) {
      setTestResults(prev => ({
        ...prev,
        [providerId]: { success: false, message: 'Chave API não configurada' }
      }));
      return;
    }

    try {
      setTesting(prev => ({ ...prev, [providerId]: true }));
      setTestResults(prev => ({ ...prev, [providerId]: null }));

      const result = await aiService?.testConnection(providerId, apiKey);
      setTestResults(prev => ({ ...prev, [providerId]: result }));

      if (result?.success) {
        // Auto-save key if test succeeds and key is new (not yet saved)
        if (apiKey && !config?.[providerId]?.hasKey) {
          const saveResult = await aiService?.saveAPIKey(providerId, apiKey, {
            enabled: true,
            model: config?.[providerId]?.model,
            usageLimit: config?.[providerId]?.usageLimit
          });

          if (saveResult?.success) {
            setConfig(prev => ({
              ...prev,
              [providerId]: { 
                ...prev?.[providerId], 
                hasKey: true, 
                enabled: true,
                connectionStatus: 'connected' 
              }
            }));
            setApiKeys(prev => ({ ...prev, [providerId]: '' }));
            window.dispatchEvent(new CustomEvent('statusUpdate', {
              detail: {
                type: 'success',
                message: `Chave ${providers.find(p => p.id === providerId)?.name} conectada e salva com sucesso`,
                progress: 100
              }
            }));
          }
        } else {
          // Just update connection status for already saved keys
          setConfig(prev => ({
            ...prev,
            [providerId]: { ...prev?.[providerId], connectionStatus: 'connected' }
          }));
        }
      }
    } catch (error) {
      console.error(`Error testing ${providerId}:`, error);
      setTestResults(prev => ({
        ...prev,
        [providerId]: { success: false, message: error?.message || 'Erro ao testar conexão' }
      }));
    } finally {
      setTesting(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const handleDisconnect = async (providerId) => {
    if (!window.confirm(`Deseja realmente desconectar ${providers?.find(p => p?.id === providerId)?.name}? A chave API será removida.`)) {
      return;
    }

    try {
      setSaving(prev => ({ ...prev, [providerId]: true }));
      const result = await aiService?.deleteAPIKey(providerId);

      if (result?.success) {
        setConfig(prev => ({
          ...prev,
          [providerId]: {
            ...prev?.[providerId],
            hasKey: false,
            enabled: false,
            connectionStatus: 'not_tested'
          }
        }));
        setTestResults(prev => ({ ...prev, [providerId]: null }));
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'success',
            message: `${providers.find(p => p.id === providerId)?.name} desconectado com sucesso`,
            progress: 100
          }
        }));
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao desconectar API',
          progress: 100
        }
      }));
    } finally {
      setSaving(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const getConnectionStatusBadge = (status) => {
    const badges = {
      connected: { color: 'bg-success/10 text-success', icon: 'CheckCircle', label: 'Conectado' },
      failed: { color: 'bg-error/10 text-error', icon: 'XCircle', label: 'Falha' },
      not_tested: { color: 'bg-muted text-muted-foreground', icon: 'HelpCircle', label: 'Não testado' }
    };
    const badge = badges?.[status] || badges?.not_tested;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${badge?.color}`}>
        <Icon name={badge?.icon} size={12} />
        {badge?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 lg:py-8">
        <BreadcrumbNavigation
          items={[
            { label: 'Dashboard', path: '/' },
            { label: 'AI Assistant Hub', path: '/ai-assistant-hub' }
          ]}
        />

        {/* Hero Section with Gradient Background */}
        <div className="relative overflow-hidden rounded-2xl mb-8 bg-gradient-to-br from-blue-600 via-blue-500 to-purple-600 p-8 lg:p-12 shadow-2xl">
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Icon name="Bot" size={32} className="text-white" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                  PAINEL DE CONTROLE IA
                </h1>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
                  <Icon name="Sparkles" size={14} className="text-white" />
                  <span className="text-sm text-white font-medium">controle de coneção de Api</span>
                </div>
              </div>
            </div>
          </div>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-400/20 rounded-full blur-3xl"></div>
        </div>

        {/* Security Banner */}
        <div className="bg-info/10 border border-info/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="Shield" size={20} color="var(--color-info)" />
            <div className="flex-1">
              <h3 className="font-semibold text-info mb-1">🔒 Segurança de Ponta a Ponta</h3>
              <p className="text-sm text-muted-foreground">
                Suas chaves API são criptografadas com AES-256 antes do armazenamento no Supabase.
                Apenas você tem acesso às chaves descriptografadas.
              </p>
            </div>
          </div>
        </div>

        {/* 3-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Column 1: Provider Configuration */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-xl font-semibold text-foreground">Configuração de Provedores</h2>
            {providers?.map(provider => (
              <div key={provider?.id} className="bg-card border border-border rounded-lg p-6 shadow-brand-sm">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 ${provider?.bgColor} rounded-lg flex items-center justify-center`}>
                      <Icon name={provider?.icon} size={24} className={provider?.color} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{provider?.name}</h3>
                      <p className="text-xs text-muted-foreground">{provider?.description}</p>
                    </div>
                  </div>
                  {getConnectionStatusBadge(config?.[provider?.id]?.connectionStatus)}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Chave API
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Input
                          type={showKeys?.[provider?.id] ? 'text' : 'password'}
                          value={apiKeys?.[provider?.id]}
                          onChange={(e) => setApiKeys(prev => ({ ...prev, [provider?.id]: e?.target?.value }))}
                          placeholder={config?.[provider?.id]?.hasKey ? '••••••••••••' : 'Cole sua chave API aqui'}
                          className="pr-10"
                          disabled={config?.[provider?.id]?.hasKey}
                        />
                        <button
                          onClick={() => setShowKeys(prev => ({ ...prev, [provider?.id]: !prev?.[provider?.id] }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <Icon name={showKeys?.[provider?.id] ? 'EyeOff' : 'Eye'} size={16} />
                        </button>
                      </div>
                      {config?.[provider?.id]?.hasKey ? (
                        <Button
                          onClick={() => handleDisconnect(provider?.id)}
                          disabled={saving?.[provider?.id]}
                          variant="outline"
                          className="border-error text-error hover:bg-error/10"
                        >
                          {saving?.[provider?.id] ? (
                            <Icon name="Loader" size={16} className="animate-spin" />
                          ) : (
                            <Icon name="Trash2" size={16} />
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleSaveKey(provider?.id)}
                          disabled={!apiKeys?.[provider?.id] || saving?.[provider?.id]}
                          variant="primary"
                        >
                          {saving?.[provider?.id] ? (
                            <Icon name="Loader" size={16} className="animate-spin" />
                          ) : (
                            <Icon name="Save" size={16} />
                          )}
                        </Button>
                      )}
                    </div>
                    <a
                      href={provider?.docsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline mt-1 inline-block"
                    >
                      Obter chave API →
                    </a>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      label="Modelo"
                      options={provider?.models}
                      value={config?.[provider?.id]?.model}
                      onChange={(value) => setConfig(prev => ({
                        ...prev,
                        [provider?.id]: { ...prev?.[provider?.id], model: value }
                      }))}
                    />
                    <Input
                      label="Limite de Uso"
                      type="number"
                      value={config?.[provider?.id]?.usageLimit}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        [provider?.id]: { ...prev?.[provider?.id], usageLimit: parseInt(e?.target?.value) }
                      }))}
                    />
                  </div>

                  <Button
                    onClick={() => handleTestConnection(provider?.id)}
                    disabled={testing?.[provider?.id] || (!apiKeys?.[provider?.id] && !config?.[provider?.id]?.hasKey)}
                    variant="outline"
                    className="w-full"
                  >
                    {testing?.[provider?.id] ? (
                      <>
                        <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                        Testando...
                      </>
                    ) : (
                      <>
                        <Icon name="Zap" size={16} className="mr-2" />
                        Testar Conexão
                      </>
                    )}
                  </Button>

                  {testResults?.[provider?.id] && (
                    <div className={`p-3 rounded-md ${testResults?.[provider?.id]?.success ? 'bg-success/10 border border-success/20' : 'bg-error/10 border border-error/20'}`}>
                      <div className="flex items-center gap-2">
                        <Icon
                          name={testResults?.[provider?.id]?.success ? 'CheckCircle' : 'XCircle'}
                          size={16}
                          className={testResults?.[provider?.id]?.success ? 'text-success' : 'text-error'}
                        />
                        <span className="text-sm font-medium">
                          {testResults?.[provider?.id]?.message}
                        </span>
                        {testResults?.[provider?.id]?.success && testResults?.[provider?.id]?.responseTime && (
                          <span className="text-xs text-muted-foreground ml-auto">
                            {testResults?.[provider?.id]?.responseTime}ms
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Column 3: AI Capabilities & Quick Actions */}
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6 shadow-brand-sm">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Sparkles" size={18} />
                Recursos de IA
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
                  <Icon name="FileText" size={16} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Geração de Conteúdo</p>
                    <p className="text-xs text-muted-foreground">Títulos e descrições otimizados</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
                  <Icon name="Package" size={16} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Sugestões de Kits</p>
                    <p className="text-xs text-muted-foreground">Combinações inteligentes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
                  <Icon name="TrendingUp" size={16} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Análise de Preços</p>
                    <p className="text-xs text-muted-foreground">Precificação competitiva</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
                  <Icon name="Target" size={16} className="text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Recomendações</p>
                    <p className="text-xs text-muted-foreground">Marketplaces ideais</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-6">
              <h3 className="font-semibold text-foreground mb-2">💡 Dica Pro</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Use o Gemini 2.0 Flash (gratuito) para análises rápidas e economize custos!
              </p>
              <Button
                onClick={() => navigate('/product-catalog-manager')}
                variant="primary"
                className="w-full"
              >
                <Icon name="Sparkles" size={16} className="mr-2" />
                Ir para Catálogo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantHub;